import { PROJECT_AUDIO_TYPES } from 'consts/audio'
import { IAudioItem, TAudioType } from 'types/audio'
import { IAudioEffect, TAudioFadeEffectType } from 'types/common'

interface IParams {
  assetId: string
  duration: number
  path: string
  title: string
  type: TAudioType
  projectStart: number
  lineIndex: number
}

export const generateAndGetAudioDefaultDataForProject = (data: IParams) => {
  const { assetId, duration, path, title, type, projectStart, lineIndex } = data

  return {
    id: crypto.randomUUID(),
    volume: 100,
    mediaStart: 0,
    lineIndex,
    assetId,
    duration,
    mediaDuration: duration,
    mediaPath: path,
    title,
    type,
    projectStart
  }
}

const isUserUploadedAudio = (audio: IAudioItem) =>
  audio.mediaPath.includes('uploads')

export const isAudioFromGenres = (audio: IAudioItem) =>
  audio.type === 'SOUND' && !isUserUploadedAudio(audio)
export const isAudioItem = (type: string | null): boolean => {
  const audioTypes = Object.values(PROJECT_AUDIO_TYPES) as string[]
  return type ? audioTypes.includes(type) : false
}

export const isUploadingAudio = (audio: IAudioItem) =>
  audio.mediaPath.includes('blob') && audio.type === PROJECT_AUDIO_TYPES.SOUND

// A fade effect can occupy at most half of the item's playback duration.
export const MAX_FADE_DURATION_RATIO = 0.5

export const AUDIO_EFFECT_STEP_COEFFICIENT = 1

const FADE_STEP_FACTOR = 10 ** AUDIO_EFFECT_STEP_COEFFICIENT

// Half the clip, floored to the slider's step grid. Flooring (not rounding)
// keeps the cap at or below duration/2 and on a value the fade slider can
// actually reach, so the slider max and the clamp below always agree.
export const getMaxFadeDuration = (itemDuration: number): number =>
  Math.floor(itemDuration * MAX_FADE_DURATION_RATIO * FADE_STEP_FACTOR) /
  FADE_STEP_FACTOR

// Clamps every fade effect's duration to the item's current max (duration / 2).
// Run whenever an item's duration shrinks (trim, playback-rate change) so a fade
// never outlasts half the clip — independent of whether the fade panel is open.
// Returns the same array reference when nothing changed.
export const clampFadeEffectsToDuration = (
  effects: IAudioEffect[] | undefined,
  itemDuration: number
): IAudioEffect[] | undefined => {
  if (!effects?.length) {
    return effects
  }
  const maxFadeDuration = getMaxFadeDuration(itemDuration)
  let didClamp = false
  const clampedEffects = effects.map(effect => {
    if (effect.key === 'fade' && effect.meta.duration > maxFadeDuration) {
      didClamp = true
      return {
        ...effect,
        meta: { ...effect.meta, duration: maxFadeDuration }
      }
    }
    return effect
  })
  return didClamp ? clampedEffects : effects
}

// Splitting a clip creates a fresh, fade-less cut edge between the two halves.
// The left half keeps the original clip's start (so only its fade-in survives),
// the right half keeps the original clip's end (so only its fade-out survives).
// The surviving fade is then re-clamped to the half's new, shorter duration.
// Fades that don't exist are left absent — nothing is added.
export const splitFadeEffects = (
  effects: IAudioEffect[] | undefined,
  side: 'left' | 'right',
  itemDuration: number
): IAudioEffect[] | undefined => {
  if (!effects?.length) {
    return effects
  }
  const keptFadeType: TAudioFadeEffectType =
    side === 'left' ? 'fade-in' : 'fade-out'
  const survivingEffects = effects.filter(
    effect => effect.key !== 'fade' || effect.type === keptFadeType
  )
  return clampFadeEffectsToDuration(survivingEffects, itemDuration)
}
